import type { KontoRolle, TeamRolle } from "@olgen/api/auth";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, type ReactNode, useContext, useEffect, useState } from "react";
import { avatarUrl } from "@/api";
import { sitzungQuery } from "@/auth";
import { farbeFuer, initialenVon } from "@/lib/avatar";
import { aktiveVorschau, type KundenVorschau, vorschauSetzen } from "@/lib/kundenVorschau";
import { kontoRolleLabel, teamRolleLabel } from "@/lib/vokabular";
import { aktivesKontoSetzen } from "@/lib/zuletztGeoeffnet";

/**
 * Das angemeldete Konto, angereichert fürs UI: Initialen und Farbe kommen
 * deterministisch aus dem Namen (lib/avatar), das Rollen-Label aus dem
 * Vokabular. Seit T018 gibt es keine Mock-Person mehr — was hier steht, ist
 * der Mensch aus der Sitzung.
 */
export type AngemeldeterNutzer = {
	id: string;
	name: string;
	email: string;
	rolle: KontoRolle;
	/** Team-Rolle (03.09.2026) — `null` bei Kundenkonten. */
	teamRolle: TeamRolle | null;
	/** Kurz für `teamRolle === "inhaber"`: darf Team, Module und Einstellungen verwalten. */
	istInhaber: boolean;
	initials: string;
	color: string;
	/** UI-Label der Rolle („Inhaber" / „Mitarbeiter" / „Kunde"). */
	rolleLabel: string;
	/** Das Profilbild — `null` ohne Bild, dann zeigen die Avatare die Initialen. */
	bildUrl: string | null;
};

interface AppState {
	/**
	 * Die echte Konto-Rolle aus der Sitzung (T014) — der frühere
	 * Team/Kunde-Umschalter ist Geschichte. Ohne Sitzung (Anmeldeseite) gilt
	 * `kunde`, die kleinste Berechtigung; unterhalb von `_angemeldet` ist die
	 * Sitzung immer da.
	 */
	role: KontoRolle;
	theme: "light" | "dark";
	toggleTheme: () => void;
	user: AngemeldeterNutzer;
	/**
	 * Die Kunden-Vorschau (Team-Konto schaut als ein bestimmter Kunde, nur
	 * lesend) — `role` ist dann `kunde`. Starten/Beenden leeren den
	 * Query-Cache, damit alle /mein-Daten frisch für die richtige Sicht kommen.
	 */
	vorschau: KundenVorschau | null;
	vorschauStarten: (kunde: KundenVorschau) => void;
	vorschauBeenden: () => void;
}

const Ctx = createContext<AppState | null>(null);

/**
 * Erscheint nie hinter der `_angemeldet`-Schwelle (dort garantiert der Guard
 * eine Sitzung) — nur die Anmeldeseite rendert den Provider ohne Konto, und
 * sie liest `user` nicht.
 */
const OHNE_SITZUNG: AngemeldeterNutzer = {
	id: "",
	name: "",
	email: "",
	rolle: "kunde",
	teamRolle: null,
	istInhaber: false,
	initials: "",
	color: "var(--color-surface-2)",
	rolleLabel: "",
	bildUrl: null,
};

export function AppProvider({ children }: { children: ReactNode }) {
	const sitzung = useQuery(sitzungQuery);
	const queryClient = useQueryClient();
	const [theme, setTheme] = useState<"light" | "dark">("light");

	useEffect(() => {
		document.documentElement.classList.toggle("dark", theme === "dark");
	}, [theme]);

	const konto = sitzung.data ?? null;
	// „Zuletzt geöffnet" hängt am Konto, nicht am Browser (lib/zuletztGeoeffnet).
	useEffect(() => {
		aktivesKontoSetzen(konto?.id ?? null);
	}, [konto?.id]);
	const user: AngemeldeterNutzer =
		konto === null
			? OHNE_SITZUNG
			: {
					id: konto.id,
					name: konto.name,
					email: konto.email,
					rolle: konto.rolle,
					teamRolle: konto.teamRolle,
					istInhaber: konto.teamRolle === "inhaber",
					initials: initialenVon(konto.name),
					color: farbeFuer(konto.name),
					rolleLabel:
						konto.teamRolle === null
							? kontoRolleLabel[konto.rolle]
							: teamRolleLabel[konto.teamRolle],
					bildUrl:
						konto.bild === null ? null : avatarUrl({ userId: konto.id, version: konto.bild }),
				};

	const [vorschau, setVorschau] = useState<KundenVorschau | null>(() => aktiveVorschau());
	// Nur ein Team-Konto kann Vorschau — ein Kundenkonto bleibt Kunde.
	const aktiv = user.rolle === "mitarbeiter" ? vorschau : null;

	const value: AppState = {
		role: aktiv === null ? user.rolle : "kunde",
		theme,
		toggleTheme: () => setTheme((t) => (t === "light" ? "dark" : "light")),
		user,
		vorschau: aktiv,
		vorschauStarten: (kunde) => {
			vorschauSetzen(kunde);
			setVorschau(kunde);
			queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== "sitzung" });
		},
		vorschauBeenden: () => {
			vorschauSetzen(null);
			setVorschau(null);
			queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== "sitzung" });
		},
	};
	return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useApp() {
	const ctx = useContext(Ctx);
	if (!ctx) throw new Error("useApp must be used within AppProvider");
	return ctx;
}
